/**
 * Color map tokens — hex values for non-CSS-variable use cases.
 * Use CSS variables (--text-primary etc.) in JSX for themeable colors.
 * Use these hex constants only for: canvas/SVG rendering, inline style maps, non-themeable UI.
 * @layer-1 — shared utilities.
 */

/** Password strength score → hex color (for bar indicators and text). */
export const strengthColors: Record<0 | 1 | 2 | 3 | 4 | 5, string> = {
  0: "#E4E3E0", // empty bar
  1: "#B84C3A", // weak — red
  2: "#A87060", // fair — muted terracotta
  3: "#A87060", // good
  4: "#4A7C59", // strong — muted green
  5: "#4A7C59", // very strong
};

/**
 * 4-bar strength display colors — maps bar index (0–3) to hex.
 * RegisterPage has 4 bars derived from 5 rules (score 0–3).
 */
export const strengthBarColors4: string[] = [
  "#E8E5DE", // 0 bars — empty
  "#B84C3A", // 1 bar — weak
  "#C4956A", // 2 bars — terracotta
  "#5C8C5A", // 3 bars — strong
];

/** Admin dashboard-specific palette tokens. */
export const adminPalette = {
  mutedTerracotta: "#A87060",   // progress bars, secondary labels
  successDark: "#2D5E35",        // published status badge text
  pendingText: "#8B4C2A",       // pending status badge text
  publishedBg: "#F0F7F2",        // published badge background
  pendingBg: "#FDF5F0",          // pending badge background
  surfaceWarm: "#F7F6F3",       // draft badge background, hover rows
} as const;

/** Auth page color palette — raw hex for dividers, dots, inline SVG, canvas. */
export const authPalette = {
  terracotta: "#C4956A",   // brand accent, active states
  success: "#5C8C5A",       // verified, passed checks
  error: "#B84C3A",        // errors, weak strength
  warning: "#D4823E",      // fair strength, caution
  warmPaper: "#FAF8F5",    // page background
  white: "#FFFFFF",        // card surfaces, button text
  ink: "#1A1917",           // primary text/buttons
  inkHover: "#2C2B29",     // hover state for ink
  cardShadow: "0 2px 20px rgba(26,25,23,0.06)", // card elevation shadow
  borderLight: "#E8E5DE", // card borders
  borderMid: "#DDD9D2",   // input borders
  divider: "#EDEBE6",      // section dividers
  mutedBorder: "#C9C5BE", // checkbox unchecked border
  surface: "#F4F3F0",     // hover fill, secondary surfaces
  surfaceLow: "#F0EFEC",  // progress bar track, subtle backgrounds
  surfaceCard: "#F5F3F0", // card inner backgrounds
  muted: "#9C9A96",       // inactive nav items, placeholder text
  mutedDim: "#444444",    // very muted text on light backgrounds
  textOnLight: "rgba(26,25,23,0.4)",    // secondary label text
  textOnLightDim: "rgba(26,25,23,0.25)", // icon/arrow dimmed
  // Reconfirmation banner tinted background
  terracottaTint: "rgba(196,149,106,0.08)",
  terracottaBorder: "rgba(196,149,106,0.25)",
  // Error tinted background
  errorTint: "rgba(184,76,58,0.06)",
  errorBorder: "rgba(184,76,58,0.2)",
  // Checkbox hover tint
  terracottaHover: "rgba(196,149,106,0.04)",
  // Spinner transparent border (used with rgba)
  spinnerLight: "rgba(255,255,255,0.3)",
} as const;

export type AuthPalette = typeof authPalette;